import React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Controlled confirm dialog. Use as a replacement for window.confirm().
 *
 * Usage:
 *   const [confirm, setConfirm] = useState(null);
 *   <ConfirmDialog
 *     open={!!confirm}
 *     title="Delete realtor?"
 *     description="This cannot be undone."
 *     confirmLabel="Delete"
 *     destructive
 *     onConfirm={confirm?.onConfirm}
 *     onClose={() => setConfirm(null)}
 *   />
 */
export default function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title = "Are you sure?",
  description = "",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  confirmDisabled = false,
  testid = "confirm-dialog",
}) {
  const handleConfirm = async () => {
    if (confirmDisabled) return;
    try {
      if (onConfirm) await onConfirm();
    } finally {
      onClose && onClose();
    }
  };
  return (
    <AlertDialog open={!!open} onOpenChange={(o) => !o && onClose && onClose()}>
      <AlertDialogContent data-testid={testid}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description ? (
            <AlertDialogDescription asChild>
              <div>{description}</div>
            </AlertDialogDescription>
          ) : null}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => onClose && onClose()}
            data-testid={`${testid}-cancel`}
            className="min-h-[44px]"
          >
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={confirmDisabled}
            data-testid={`${testid}-confirm`}
            className={`min-h-[44px] text-white ${
              destructive ? "bg-red-600 hover:bg-red-700" : ""
            } ${confirmDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
            style={
              destructive
                ? undefined
                : { background: "hsl(var(--brand))" }
            }
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
